/**
 * Source matching for inline citations against RAG source lists.
 */

import { normalizeDocHint, normalizeSerffId } from './answersDocumentService';

const docHintMatchesSource = (hintRaw, source) => {
  const normalized = normalizeDocHint(hintRaw);
  if (!normalized) return true;
  const pool = `${source.file_name || ''} ${source.file_path || ''}`.toLowerCase();

  if (/\bact\s*memo\b/i.test(normalized) || /\bactuarial\s+memorandum\b/i.test(normalized)) {
    if (!/act_memo|act memo|act-memo|actuarial/.test(pool)) return false;
  }

  const iccMatches = normalized.match(/ICC\d{2}-\d{5}/gi) || [];
  for (const icc of iccMatches) {
    const lo = icc.toLowerCase();
    if (!pool.includes(lo) && !pool.includes(lo.replace(/-/g, '_'))) return false;
  }

  const hint = normalized.toLowerCase();
  const basename = hint.includes('/') ? hint.split('/').pop() : hint;
  if (iccMatches.length > 0 && /\bact\s*memo\b/i.test(normalized)) return true;
  if (iccMatches.length > 0) return true;

  return (
    pool.includes(hint)
    || pool.includes(hint.replace(/\s+/g, '_'))
    || (basename && (pool.includes(basename) || pool.includes(basename.replace(/\s+/g, '_'))))
  );
};

const pickBestSourceIndex = (indices, sources, docHint) => {
  if (!indices.length) return -1;
  if (indices.length === 1) return indices[0];
  const wantActMemo = /\bact\s*memo\b/i.test(docHint || '');
  const score = (idx) => {
    const path = `${sources[idx].file_path || ''} ${sources[idx].file_name || ''}`.toLowerCase();
    return path.length + (wantActMemo && (path.includes('act_memo') || path.includes('act memo')) ? 1000000 : 0);
  };
  return indices.reduce((best, idx) => (score(idx) >= score(best) ? idx : best), indices[0]);
};

export const findSourceIndex = (sources, serffId, pageStart, pageEnd, docHint) => {
  const sid = normalizeSerffId(serffId);
  const ps = Number(pageStart);
  const pe = Number(pageEnd);
  const hint = normalizeDocHint(docHint);
  const hasHint = !!hint;
  const matchSerff = (source) => normalizeSerffId(source.serff_id) === sid;
  const strictPages = (source) => Number(source.page_start) === ps && Number(source.page_end) === pe;
  const overlap = (source) => Number(source.page_start) <= pe && Number(source.page_end) >= ps;

  const collect = (pagePredicate, useDocHint) => {
    const out = [];
    for (let i = 0; i < sources.length; i += 1) {
      if (!matchSerff(sources[i]) || !pagePredicate(sources[i])) continue;
      if (useDocHint && !docHintMatchesSource(hint, sources[i])) continue;
      out.push(i);
    }
    return out;
  };

  let matches = collect(strictPages, hasHint);
  if (matches.length) return pickBestSourceIndex(matches, sources, hint);

  if (hasHint) {
    matches = collect(overlap, true);
    return matches.length ? pickBestSourceIndex(matches, sources, hint) : -1;
  }

  matches = collect(strictPages, false);
  if (matches.length) return pickBestSourceIndex(matches, sources, hint);

  matches = collect(overlap, false);
  return matches.length ? pickBestSourceIndex(matches, sources, hint) : -1;
};
